/**
 * Tipos compartidos del dominio inglés que cruzan frontera client ↔ backend.
 *
 * Single source of truth para los 6 stops semánticos de intensidad. Cualquier
 * redefinición local (union literal, array de strings, zod enum hardcoded)
 * es drift y debe importar desde acá.
 *
 * `starter` es el piso para el principiante absoluto (pre-A1): el coach usa
 * español como andamiaje. De `cozy` en adelante es todo inglés, cada vez más
 * exigente. El orden del array es de menor a mayor dificultad.
 */

export const ENGLISH_INTENSITIES = [
  'starter',
  'cozy',
  'natural',
  'push',
  'stretch',
  'native',
] as const;

export type EnglishIntensity = (typeof ENGLISH_INTENSITIES)[number];

export function isEnglishIntensity(value: unknown): value is EnglishIntensity {
  return (
    typeof value === 'string' && (ENGLISH_INTENSITIES as readonly string[]).includes(value)
  );
}

/**
 * Semillas de conversación para el MODO LIBRE (docs/speak-plan). El user, desde
 * un menú sutil en la pantalla de conversación, le da una DIRECCIÓN a la charla
 * sin salir de modo libre: el coach inyecta un "FOCO DE LA CHARLA" en su system
 * prompt (ver `seedFocusBlock` en el módulo languages) y abre/pivota hacia ahí.
 *
 * Contrato cliente ↔ backend: sólo los IDs viajan (query param `seed` al
 * arrancar, o `sessionUpdate { seed }` mid-charla). Los labels/emojis del menú
 * son chrome de UI y viven en `apps/speak`. El texto del foco que ve el coach
 * vive backend-only en `packages/modules/languages`.
 *
 * NO incluye "una situación" (eso es un roleplay → setea un `scenarioId`, no un
 * seed) ni "noticias" (coming soon, depende del news-service). Esos son entries
 * del menú que la UI maneja por fuera de este contrato.
 *
 * Orden = el sugerido en el menú. Los últimos 4 son JUEGOS (ver `GAME_SEEDS`):
 * viajan por el mismo contrato pero el coach los abre como juego explícito.
 */
export const CONVERSATION_SEEDS = [
  'big-ideas',
  'debate',
  'tell-me',
  'storytelling',
  'expression',
  'yours',
  'translate-it',
  'guess-it',
  'describe-it',
  'three-words',
  'time-machine',
] as const;

export type ConversationSeed = (typeof CONVERSATION_SEEDS)[number];

export function isConversationSeed(value: unknown): value is ConversationSeed {
  return (
    typeof value === 'string' && (CONVERSATION_SEEDS as readonly string[]).includes(value)
  );
}

/**
 * Subconjunto de semillas que son JUEGOS de práctica (categoría "Juegos" del
 * menú de direcciones): dinámicas lúdicas con reglas explícitas, no temas de
 * charla. A diferencia de un seed temático (que el coach "abre natural" sin
 * anunciarlo), un juego se ANUNCIA y arranca con una primera movida del coach.
 *
 * `describe-it` tiene además una palabra secreta que vive SOLO en el cliente
 * (se muestra en pantalla y JAMÁS viaja al backend): el coach no la conoce y
 * la adivina en serio — esa asimetría es el juego.
 */
export const GAME_SEEDS = [
  'translate-it',
  'guess-it',
  'describe-it',
  'three-words',
  'time-machine',
] as const satisfies readonly ConversationSeed[];

export type GameSeed = (typeof GAME_SEEDS)[number];

export function isGameSeed(value: unknown): value is GameSeed {
  return typeof value === 'string' && (GAME_SEEDS as readonly string[]).includes(value);
}

/**
 * Progreso DERIVADO de un escenario para un usuario. No es estado materializado:
 * se proyecta de los veredictos de objetivos (`language_scenario_objectives`)
 * cruzados con la intensidad de cada sesión (`language_session_metrics`). Lo
 * consumen el hub (badges de completado) y el debrief (CTA "rejugar más alto").
 * Ver docs/speak-plan/12 (P1).
 */
export interface ScenarioProgress {
  scenarioId: string;
  /**
   * Intensidad más alta en la que se cumplieron TODOS los primary activos de
   * esa corrida. `null` = nunca completado (jugado pero sin misión entera).
   * El orden es el global de `ENGLISH_INTENSITIES`.
   */
  bestCompletedIntensity: EnglishIntensity | null;
  /** Mejor ratio de primary cumplidos en cualquier corrida (0..1) → "2/3". */
  bestPrimaryRatio: number;
  /** Cantidad de sesiones jugadas de este escenario. */
  timesPlayed: number;
  /** ISO8601 de la última vez que se jugó, o `null` si nunca. */
  lastPlayedAt: string | null;
}

/** Cómo se practica un escenario: con respuesta modelo para decir, o solo. */
export const SCENARIO_MODES = ['solo', 'assisted'] as const;
export type ScenarioMode = (typeof SCENARIO_MODES)[number];

/**
 * El modo con el que arranca la pantalla previa de un escenario (regla de
 * Emmanuel, ajustada el 13/10/2026 tras probar la etapa 1): lo que el alumno
 * ELIGIÓ (`conversationPrefs.scenarioMode`) vale para todos los escenarios
 * hasta que lo cambie; mientras nunca eligió, decide el nivel: A0–A2
 * «Asistida», B1–C2 «Solo». La opción aparece siempre, en todos los niveles.
 */
export function defaultScenarioMode(cefr: string, chosen: ScenarioMode | null): ScenarioMode {
  if (chosen) return chosen;
  if (cefr === 'A0' || cefr === 'A1' || cefr === 'A2') return 'assisted';
  if (cefr === 'B1' || cefr === 'B2' || cefr === 'C1' || cefr === 'C2') return 'solo';
  throw new Error(`defaultScenarioMode: nivel CEFR desconocido «${cefr}»`);
}
